import marketing from '@/lib/marketing'
import { httpGetJson, httpPostJson, httpPutJson } from '@/lib/net'

import type { Subscription } from '@/types/subscriptions'
import { Vertical } from '@/types/verticals'
import { reactive } from 'vue'
import split from '@/lib/split'
import { isActive } from '@/services/feature-flag-service'
import { hasValidUserSession } from '@/services/identity-client'
import { warn } from '@/lib/logger'

// Data Interfaces

interface StateLocations {
  locations?: StateInfo[]
}

export interface StateInfo {
  countrycode: string
  subdivision: string | null
}

// API Interfaces

interface CreateTrialReqest {
  referral_campaign_id?: string
  referrer_account_id?: string
  state: string
}

interface CreateTrialResponse {
  token: string
}

interface GetTrialsResponse {
  remaining: number
  used: number
}

interface SetStateRequest {
  state: string
  onboarding_step?: number
}

interface SetStateResponse {
  token: string
}

interface SetStateLocationRequest {
  locations: StateInfo[]
  onboarding_step?: number
}

interface SetStateLocationResponse {
  locations: StateInfo[]
}

/**
 *  Time frame how long a user/membership is considered new
 *  (7 days)
 */
export const NEW_MEMBERSHIP_TIME_FRAME = 7 * 24 * 60 * 60 * 1000

/**
 * Service for modifying, storing, and sharing the state of the current subscriptions
 */
class SubscriptionService {
  /**
   * Subscriptions by application.
   */
  subscriptions: { [key in Vertical]?: Subscription }

  /**
   * User's subscribed to states
   */
  states: StateLocations

  fetchStatesPromise?: Promise<StateLocations>
  subscriptionsReadyResolver!: () => void
  subscriptionsReadyPromise!: Promise<void>

  ready: boolean = false

  constructor() {
    this.subscriptions = {}
    this.states = {}

    // promise to wait for subscriptions to be ready
    this.subscriptionsReadyPromise = new Promise(
      (resolve) => (this.subscriptionsReadyResolver = resolve)
    )
  }

  /**
   * Requests from the server and sets the user subscriptions.
   */
  async syncSubscriptions() {
    this.ready = false
    if (await hasValidUserSession()) {
      return httpGetJson<typeof this.subscriptions>('/v2/subscriptions').then(
        (subscriptions) => {
          this.subscriptions = subscriptions
          this.ready = true
          this.subscriptionsReadyResolver()
          return this.subscriptions
        }
      )
    }
    warn(
      'Subscription',
      'No identity user present in session, clearing subscriptions'
    )
    this.subscriptions = {}
  }

  /**
   * Starts a trial for the user.
   */
  startTrial(state = '') {
    const body: CreateTrialReqest = { state }
    return httpPostJson<CreateTrialResponse>('/v1/trials', body)
  }

  /**
   * Get the user subscriptions for all apps.
   *
   * @returns Subscriptions for all onX apps.
   */
  getSubscriptions() {
    return this.subscriptions
  }

  /**
   * Starts a trial if the user doesn't have any used yet
   */
  async startTrialIfNoneUsed() {
    let trials: GetTrialsResponse | null = null

    try {
      trials = await httpGetJson<GetTrialsResponse>('/v1/trials')
    } catch {
      /* pass */
    }

    if (trials?.used === 0) {
      // Get referral data and await splitClientsReady before checking full autosub split
      const isReferral = !!localStorage.getItem('referral_data')
      await split.splitClientsReady()
      if (isActive('gd_disable_onx_trial', { is_referral: isReferral })) {
        return
      }
      await this.startTrial()

      // Send trial started event
      marketing.trialStarted()

      await this.syncSubscriptions()
      return true
    }
    return false
  }

  /**
   * Get subscriptions for a given app
   *
   * @param app - the app that we want subscriptions for. Defaults to the current app.
   * @returns the subscriptions object for the app
   */
  getSubscriptionsForApp(app = Vertical.Fish) {
    return this.getSubscriptions()[app]
  }

  /**
   * Determines if there are any active subscriptions for the current app,
   * including trials. If this is false, it indicates a "Basic" user.
   *
   * @returns Whether there are any active subscriptions.
   */
  hasActiveSubscription() {
    return this.getMembershipLevel() !== 'basic'
  }

  /**
   * Gets the user's membership level for the current app
   *
   * @returns the user's current membership level
   */
  getMembershipLevel() {
    return this.getSubscriptionsForApp()?.membership_level ?? 'basic'
  }

  /**
   * Gets the user's states from /v2/state API if they have a hunt membership
   *
   * @returns the user's current states
   */
  getHuntStates(): StateInfo[] {
    return this.states.locations || []
  }

  /**
   * Gets the user's state from the subscriptions API if they have a hunt membership
   *
   * @returns the user's current state
   */
  getHuntState() {
    const membership = this.getSubscriptionsForApp()
    return membership?.state ? membership.state : ''
  }

  /**
   * Sets the state for a Hunt account
   * @param selectedState state id to set for Hunt account (ex. "mt", "me", "wa")
   * @param onboardingStepNumber
   */
  async setHuntState(selectedState: string, onboardingStepNumber?: number) {
    const body: SetStateRequest = { state: selectedState }
    if (onboardingStepNumber) {
      body.onboarding_step = onboardingStepNumber
    }

    try {
      await httpPostJson<SetStateResponse>('/v1/state', body)

      await this.syncSubscriptions()
    } catch {
      // do nothing if fails
    }
  }

  /**
   * Sets the states for a Hunt multi-state account
   * @param selectedStates - Object array of states to update
   * @param onboardingStepNumber
   */
  async setHuntStates(
    selectedStates: StateInfo[],
    onboardingStepNumber?: number
  ) {
    const body: SetStateLocationRequest = { locations: selectedStates }
    if (onboardingStepNumber) {
      body.onboarding_step = onboardingStepNumber
    }

    try {
      const newStates = await httpPutJson<SetStateLocationResponse>(
        '/v2/state',
        body
      )
      if (newStates.locations) {
        this.states = newStates
      }
    } catch {
      throw new Error('Failed setting states')
    }
  }

  /**
   * Gets the subscribed states for a user
   *
   * @returns the user's currently subscribed to statess
   */
  async fetchStates() {
    if (this.fetchStatesPromise) {
      return this.fetchStatesPromise
    }
    this.fetchStatesPromise = httpGetJson<typeof this.states>('/v2/state')
    const states = await this.fetchStatesPromise
    this.states = states

    return this.states
  }

  /**
   * Get a simple array of all states
   * @returns simplified array of states (ex: ['mt', 'ca'])
   */
  getStatesArray(): string[] {
    return this.getHuntStates()
      .map((state) => state.subdivision)
      .filter((state): state is string => !!state)
  }

  /**
   * Check if user has access to private land
   * @param state - state short code (ex: 'mt' or 'ca')
   * @returns whether a user has access to private land
   */
  hasAccessToPrivateLand() {
    if (this.isBasic()) {
      return false
    }
    if (this.isElite() || this.isEliteTrial() || this.isEmployee()) {
      return true
    }
    return false
  }

  /**
   * Gets the user's trial membership level for the current app
   *
   * @returns the user's current trial membership level
   */
  getTrialLevel() {
    const subscription = this.getSubscriptionsForApp()

    if (subscription?.is_in_intro_offer_period) {
      return `as_trial.${subscription?.membership_level}`
    }

    if (subscription?.type !== 'trial') {
      return null
    }

    if (subscription?.trial_level?.toLowerCase() === 'elite') {
      return 'trial.elite'
    }

    return 'trial.premium'
  }

  /**
   * Determine's if the user has new membership by a predefined time frame
   *
   * @returns Whether user's membership is new or not
   */
  isNewMembership() {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const startedAt = this.getSubscriptionsForApp()!.started_at || 0
    const membershipStartedTime = new Date(startedAt).getTime()
    const todayTime = new Date().getTime()
    return membershipStartedTime + NEW_MEMBERSHIP_TIME_FRAME > todayTime
  }

  /**
   * Is the current user an Employee subscription?
   */
  isEmployee() {
    const membership = this.getMembershipLevel()
    return membership === 'employee'
  }

  /**
   * Is the current user a Basic subscription?
   */
  isBasic() {
    const membership = this.getMembershipLevel()
    return membership === 'basic'
  }

  /**
   * Is the current user a Premium subscription?
   */
  isPremium() {
    const membership = this.getMembershipLevel()
    return membership === 'premium'
  }

  /**
   * Is the current user a Premium multi-state subscription?
   */
  isPremiumMultiState() {
    const membership = this.getMembershipLevel()
    return membership === 'premium_multi_state'
  }

  /**
   * Is the current user an Elite subscription?
   */
  isElite() {
    const membership = this.getMembershipLevel()
    return membership === 'elite'
  }

  /**
   * Is the current user a Trial subscription?
   */
  isTrial() {
    const membership = this.getMembershipLevel()
    return membership === 'trial'
  }

  /**
   * Is the current user a Premium Trial subscription?
   */
  isPremiumTrial() {
    return this.isTrial() && this.getTrialLevel() === 'trial.premium'
  }

  /**
   * Is the current user a Elite Trial subscription?
   */
  isEliteTrial() {
    return this.isTrial() && this.getTrialLevel() === 'trial.elite'
  }
}

export default reactive(new SubscriptionService())
